/**
 * Location Service - Browser Geolocation API wrapper for GPS tracking
 * Handles permission requests, location capture, and distance calculations
 */

/**
 * Request location permission from the browser
 * @returns {Promise<boolean>} true if permission granted
 */
export const requestLocationPermission = async () => {
  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by this browser');
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { timeout: 5000 }
    );
  });
};

/**
 * Get current location from GPS
 * @param {Object} options - Geolocation options
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number, timestamp: Date}>}
 */
export const getCurrentLocation = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 15000, // 15 seconds
      maximumAge: 60000, // 1 minute cache
      ...options,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: Math.round(position.coords.accuracy), // Convert to meters (integer)
          timestamp: new Date(position.timestamp),
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timeout. Please try again.';
            break;
          default:
            errorMessage = `Location error: ${error.message}`;
            break;
        }
        reject(new Error(errorMessage));
      },
      defaultOptions
    );
  });
};

/**
 * Watch position changes (for continuous tracking if needed)
 * @param {Function} callback - Called with location updates
 * @param {Object} options - Geolocation options
 * @returns {number} watchId - ID to stop watching
 */
export const watchPosition = (callback, options = {}) => {
  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by this browser');
  }

  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 60000,
    ...options,
  };

  return navigator.geolocation.watchPosition(
    (position) => {
      callback({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Math.round(position.coords.accuracy),
        timestamp: new Date(position.timestamp),
      });
    },
    (error) => {
      console.error('GPS watch error:', error);
    },
    defaultOptions
  );
};

/**
 * Stop watching position
 * @param {number} watchId - ID returned from watchPosition
 */
export const stopWatchingPosition = (watchId) => {
  if (navigator.geolocation && watchId) {
    navigator.geolocation.clearWatch(watchId);
  }
};

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters (rounded)
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance);
};

/**
 * Check if location is within radius of target location
 * @param {number} lat1 - Current latitude
 * @param {number} lon1 - Current longitude
 * @param {number} lat2 - Target latitude
 * @param {number} lon2 - Target longitude
 * @param {number} radiusMeters - Radius in meters (default: 75)
 * @returns {boolean} true if within radius
 */
export const isWithinPropertyRadius = (lat1, lon1, lat2, lon2, radiusMeters = 75) => {
  if (!lat2 || !lon2) return false; // No property coordinates
  const distance = calculateDistance(lat1, lon1, lat2, lon2);
  return distance <= radiusMeters;
};

/**
 * Convert degrees to radians
 * @private
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Format distance for display
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance string
 */
export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${meters} m`;
  }
  const kilometers = (meters / 1000).toFixed(2);
  return `${kilometers} km`;
};

/**
 * Check if GPS is available
 * @returns {boolean} true if geolocation is supported
 */
export const isGPSAvailable = () => {
  return !!navigator.geolocation;
};

/**
 * Check browser geolocation permission state when supported
 * @returns {Promise<'granted'|'denied'|'prompt'|'unsupported'|'unknown'>}
 */
export const getLocationPermissionState = async () => {
  if (!navigator.geolocation) {
    return 'unsupported';
  }
  if (!navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'unknown';
  }
};
